import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { appelsOffresService } from '@/services';
import { useToast } from "@/hooks/use-toast";
import Layout from "@/components/Layout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar, FileText, Clock, Grid, CalendarDays, TableIcon, BarChart3 } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth } from "date-fns";
import { fr } from "date-fns/locale";
import GanttView from "@/components/GanttView";
interface AppelOffre {
  id: string;
  titre: string;
  reference: string;
  date_limite: string;
  date_publication?: string;
  date_debut_redaction_ao?: string;
  date_qr_soumissionnaires?: string;
  date_reponses_qr?: string;
  date_analyse_offres?: string;
  date_debut_soutenance?: string;
  date_fin_soutenance?: string;
  date_adjudication?: string;
  date_contractualisation?: string;
  interlocuteur_da?: string;
}
interface MonthlyData {
  month: string;
  year: number;
  appelOffres: AppelOffre[];
}
const PlanningAO = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [appelOffres, setAppelOffres] = useState<AppelOffre[]>([]);
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [viewMode, setViewMode] = useState<'cards' | 'calendar' | 'table' | 'gantt'>('cards');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const {
    toast
  } = useToast();
  useEffect(() => {
    loadAppelsOffres();
  }, []);

  // Auto-select year with data if current year has no data
  useEffect(() => {
    if (appelOffres.length > 0) {
      const currentYear = new Date().getFullYear();
      const hasDataInCurrentYear = appelOffres.some(ao => 
        new Date(ao.date_limite).getFullYear() === currentYear
      );
      
      if (!hasDataInCurrentYear) {
        // Find the year with most recent data
        const years = appelOffres.map(ao => new Date(ao.date_limite).getFullYear());
        const uniqueYears = [...new Set(years)].sort((a, b) => b - a);
        if (uniqueYears.length > 0 && uniqueYears[0] !== selectedYear) {
          setSelectedYear(uniqueYears[0]);
        }
      }
    }
  }, [appelOffres]);

  useEffect(() => {
    generateMonthlyData();
  }, [appelOffres, selectedYear]);

  // Initialize view mode from URL parameter
  useEffect(() => {
    const view = searchParams.get('view');
    if (view && ['cards', 'calendar', 'table', 'gantt'].includes(view)) {
      setViewMode(view as 'cards' | 'calendar' | 'table' | 'gantt');
    }
  }, [searchParams]);
  const loadAppelsOffres = async () => {
    try {
      const data = await appelsOffresService.getAll();
      // Filter AOs with date_limite and map to snake_case for compatibility
      const filteredData = (data || [])
        .filter((ao: any) => ao.dateLimite)
        .map((ao: any) => ({
          id: ao.id,
          titre: ao.titre,
          reference: ao.reference,
          date_limite: ao.dateLimite,
          date_publication: ao.datePublication,
          date_debut_redaction_ao: ao.dateDebutRedactionAo,
          date_qr_soumissionnaires: ao.dateQrSoumissionnaires,
          date_reponses_qr: ao.dateReponsesQr,
          date_analyse_offres: ao.dateAnalyseOffres,
          date_debut_soutenance: ao.dateDebutSoutenance,
          date_fin_soutenance: ao.dateFinSoutenance,
          date_adjudication: ao.dateAdjudication,
          date_contractualisation: ao.dateContractualisation,
          interlocuteur_da: ao.interlocuteurDa
        }))
        .sort((a: any, b: any) => new Date(a.date_limite).getTime() - new Date(b.date_limite).getTime());
      setAppelOffres(filteredData);
    } catch (error) {
      console.error('Erreur lors du chargement des appels d\'offres:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les appels d'offres",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  const generateMonthlyData = () => {
    const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    const monthlyPlanning: MonthlyData[] = months.map((month, index) => {
      const monthAOs = appelOffres.filter(ao => {
        const dateLimite = new Date(ao.date_limite);
        return dateLimite.getFullYear() === selectedYear && dateLimite.getMonth() === index;
      }).sort((a, b) => new Date(a.date_limite).getTime() - new Date(b.date_limite).getTime());
      return {
        month,
        year: selectedYear,
        appelOffres: monthAOs
      };
    });
    setMonthlyData(monthlyPlanning);
  };
  const getStatusBadge = (dateLimite: string) => {
    const today = new Date();
    const limite = new Date(dateLimite);
    const diffDays = Math.ceil((limite.getTime() - today.getTime()) / (1000 * 3600 * 24));
    if (diffDays < 0) {
      return <Badge variant="destructive">Expiré</Badge>;
    } else if (diffDays <= 7) {
      return <Badge className="bg-warning text-warning-foreground">Urgent</Badge>;
    } else {
      return <Badge className="bg-success text-success-foreground">À venir</Badge>;
    }
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit'
    });
  };
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    const currentYear = new Date().getFullYear();
    // Always include current year and adjacent years
    years.add(currentYear - 1);
    years.add(currentYear);
    years.add(currentYear + 1);
    appelOffres.forEach(ao => {
      if (ao.date_limite) {
        years.add(new Date(ao.date_limite).getFullYear());
      }
    });
    return Array.from(years).sort((a, b) => a - b);
  }, [appelOffres]);
  const getAOsForDate = (date: Date) => {
    return appelOffres.filter(ao => isSameDay(new Date(ao.date_limite), date));
  };
  const getDateStatus = (date: Date) => {
    const aos = getAOsForDate(date);
    if (aos.length === 0) return null;
    const today = new Date();
    const hasExpired = aos.some(ao => new Date(ao.date_limite) < today);
    const hasUrgent = aos.some(ao => {
      const diffDays = Math.ceil((new Date(ao.date_limite).getTime() - today.getTime()) / (1000 * 3600 * 24));
      return diffDays <= 7 && diffDays >= 0;
    });
    if (hasExpired) return 'expired';
    if (hasUrgent) return 'urgent';
    return 'upcoming';
  };
  const handleAOClick = (aoId: string) => {
    navigate(`/appels-offres/${aoId}`);
  };
  const handleViewModeChange = (newViewMode: 'cards' | 'calendar' | 'table' | 'gantt') => {
    setViewMode(newViewMode);
    setSearchParams({
      view: newViewMode
    });
  };
  const currentMonthDate = new Date(selectedYear, selectedMonth);
  const monthStart = startOfMonth(currentMonthDate);
  const monthEnd = endOfMonth(currentMonthDate);
  const monthDays = eachDayOfInterval({
    start: monthStart,
    end: monthEnd
  });
  const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  if (loading) {
    return <Layout>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Chargement du planning...</p>
          </div>
        </div>
      </Layout>;
  }
  return <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Planning Analyse</h1>
            <p className="text-muted-foreground">
              Vue d'ensemble des échéances des appels d'offres
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* View Mode Selector */}
            <div className="flex items-center bg-muted rounded-lg p-1">
              <Button variant={viewMode === 'cards' ? 'default' : 'ghost'} size="sm" onClick={() => handleViewModeChange('cards')} className="h-8">
                <Grid className="h-4 w-4 mr-1" />
                Cartes
              </Button>
              <Button variant={viewMode === 'calendar' ? 'default' : 'ghost'} size="sm" onClick={() => handleViewModeChange('calendar')} className="h-8">
                <CalendarDays className="h-4 w-4 mr-1" />
                Calendrier
              </Button>
              <Button variant={viewMode === 'table' ? 'default' : 'ghost'} size="sm" onClick={() => handleViewModeChange('table')} className="h-8">
                <TableIcon className="h-4 w-4 mr-1" />
                Tableau
              </Button>
              <Button variant={viewMode === 'gantt' ? 'default' : 'ghost'} size="sm" onClick={() => handleViewModeChange('gantt')} className="h-8">
                <BarChart3 className="h-4 w-4 mr-1" />
                Gantt
              </Button>
            </div>
            
            {/* Year Selector */}
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="h-10 w-32 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              {availableYears.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            
            {/* Month Selector (only for calendar view) */}
            {viewMode === 'calendar' && <Select value={selectedMonth.toString()} onValueChange={value => setSelectedMonth(parseInt(value))}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((month, index) => <SelectItem key={index} value={index.toString()}>
                      {month}
                    </SelectItem>)}
                </SelectContent>
              </Select>}
          </div>
        </div>

        {viewMode === 'cards' ? <>
            {/* Planning annuel - Vue cartes */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {monthlyData.map(monthData => <Card key={monthData.month} className="card-shadow">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center">
                      <Calendar className="h-5 w-5 mr-2 text-primary" />
                      {monthData.month} {monthData.year}
                    </CardTitle>
                    <CardDescription>
                      {monthData.appelOffres.length} échéance{monthData.appelOffres.length !== 1 ? 's' : ''}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {monthData.appelOffres.length === 0 ? <div className="text-center py-4">
                        <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">Aucune échéance</p>
                      </div> : <div className="space-y-3">
                        {monthData.appelOffres.map(ao => <div key={ao.id} className="p-3 border border-card-border rounded-lg hover:bg-muted/30 transition-smooth cursor-pointer" onClick={() => handleAOClick(ao.id)}>
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center">
                                <FileText className="h-4 w-4 text-primary mr-2 mt-0.5" />
                                <div className="min-w-0 flex-1">
                                  <h4 className="font-medium text-sm truncate">{ao.titre}</h4>
                                  <p className="text-xs text-muted-foreground">{ao.reference}</p>
                                </div>
                              </div>
                              {getStatusBadge(ao.date_limite)}
                            </div>
                            
                            <div className="text-xs text-muted-foreground space-y-1">
                              <div className="flex items-center">
                                <Calendar className="h-3 w-3 mr-1" />
                                Échéance: {formatDate(ao.date_limite)}
                              </div>
                              {ao.interlocuteur_da && <div className="flex items-center">
                                  <span className="font-medium">DA:</span>
                                  <span className="ml-1 truncate">{ao.interlocuteur_da}</span>
                                </div>}
                            </div>
                          </div>)}
                      </div>}
                  </CardContent>
                </Card>)}
            </div>
          </> : viewMode === 'table' ? <>
            {/* Vue tableau planning */}
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TableIcon className="h-5 w-5 mr-2 text-primary" />
                  Planning annuel {selectedYear}
                </CardTitle>
                <CardDescription>
                  Vue d'ensemble des AO par mois avec indicateurs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-32">AO</TableHead>
                        {monthlyData.map(monthData => <TableHead key={monthData.month} className="text-center min-w-20">
                            {monthData.month.substring(0, 4)}.{String(monthData.year).substring(2)}
                          </TableHead>)}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {appelOffres.filter(ao => new Date(ao.date_limite).getFullYear() === selectedYear).map(ao => <TableRow key={ao.id} className="cursor-pointer hover:bg-muted/30" onClick={() => handleAOClick(ao.id)}>
                            <TableCell className="font-medium">
                              <div className="space-y-1">
                                <div className="text-sm font-medium truncate max-w-28 text-primary hover:underline" title={ao.titre}>
                                  {ao.titre}
                                </div>
                                <div className="text-xs text-muted-foreground truncate max-w-28" title={ao.reference}>
                                  {ao.reference}
                                </div>
                              </div>
                            </TableCell>
                            {monthlyData.map((monthData, monthIndex) => {
                      const aoInMonth = monthData.appelOffres.find(monthAo => monthAo.id === ao.id);
                      const hasPublication = ao.date_publication && new Date(ao.date_publication).getMonth() === monthIndex && new Date(ao.date_publication).getFullYear() === selectedYear;
                      const hasDebutRedaction = ao.date_debut_redaction_ao && new Date(ao.date_debut_redaction_ao).getMonth() === monthIndex && new Date(ao.date_debut_redaction_ao).getFullYear() === selectedYear;
                      return <TableCell key={monthData.month} className="text-center">
                                  {aoInMonth || hasPublication || hasDebutRedaction ? <div className="flex flex-col items-center space-y-1">
                                       <div className="flex items-center space-x-1">
                                        {hasDebutRedaction && <span className="text-lg font-bold text-orange-600">●</span>}
                                        {hasPublication && <span className="text-lg font-bold text-blue-600">O</span>}
                                        {aoInMonth && <span className="text-lg font-bold text-primary">×</span>}
                                      </div>
                                       <div className="text-xs text-muted-foreground space-y-0.5">
                                        {hasDebutRedaction && <div>Début: {formatDate(ao.date_debut_redaction_ao!)}</div>}
                                        {hasPublication && <div>Pub: {formatDate(ao.date_publication!)}</div>}
                                        {aoInMonth && <div>Limite: {formatDate(aoInMonth.date_limite)}</div>}
                                      </div>
                                    </div> : <span className="text-muted-foreground/30">-</span>}
                                </TableCell>;
                    })}
                          </TableRow>)}
                     </TableBody>
                  </Table>
                </div>
                
                {/* Légende pour la vue tableau */}
                <div className="mt-6 flex items-center justify-center gap-6">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-orange-600">●</span>
                    <span className="text-sm text-muted-foreground">Date début rédaction</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-blue-600">O</span>
                    <span className="text-sm text-muted-foreground">Date de publication</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-primary">×</span>
                    <span className="text-sm text-muted-foreground">Date limite</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </> : viewMode === 'gantt' ? <>
            {/* Vue Gantt */}
            <GanttView appelOffres={appelOffres} selectedYear={selectedYear} onAOClick={handleAOClick} />
          </> : <>
            {/* Vue calendrier */}
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <CalendarDays className="h-5 w-5 mr-2 text-primary" />
                  {months[selectedMonth]} {selectedYear}
                </CardTitle>
                <CardDescription>
                  Cliquez sur une date pour voir les échéances
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-7 gap-1 mb-4">
                  {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => <div key={day} className="p-2 text-sm font-medium text-center text-muted-foreground">
                      {day}
                    </div>)}
                </div>
                
                <div className="grid grid-cols-7 gap-1">
                  {/* Empty cells for days before month start */}
                  {Array.from({
                length: (monthStart.getDay() + 6) % 7
              }).map((_, index) => <div key={`empty-${index}`} className="p-2 h-12"></div>)}
                  
                  {/* Month days */}
                  {monthDays.map(day => {
                const dayAOs = getAOsForDate(day);
                const status = getDateStatus(day);
                const isToday = isSameDay(day, new Date());
                return <Sheet key={day.toISOString()}>
                        <SheetTrigger asChild>
                          <Button variant="ghost" className={`h-12 p-1 relative hover:bg-muted/50 ${isToday ? 'ring-2 ring-primary' : ''}`} disabled={dayAOs.length === 0}>
                            <div className="w-full h-full flex flex-col items-center justify-center">
                              <span className="text-sm">{day.getDate()}</span>
                              {status && <div className={`w-2 h-2 rounded-full mt-0.5 ${status === 'expired' ? 'bg-destructive' : status === 'urgent' ? 'bg-warning' : 'bg-success'}`} />}
                            </div>
                          </Button>
                        </SheetTrigger>
                        {dayAOs.length > 0 && <SheetContent side="right" className="w-96">
                            <SheetHeader>
                              <SheetTitle>
                                Échéances du {format(day, 'dd MMMM yyyy', {
                          locale: fr
                        })}
                              </SheetTitle>
                              <SheetDescription>
                                {dayAOs.length} appel{dayAOs.length !== 1 ? 's' : ''} d'offres
                              </SheetDescription>
                            </SheetHeader>
                            <div className="mt-6 space-y-4">
                              {dayAOs.map(ao => <div key={ao.id} className="p-4 border border-card-border rounded-lg cursor-pointer hover:bg-muted/30 transition-smooth" onClick={() => handleAOClick(ao.id)}>
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center">
                                      <FileText className="h-4 w-4 text-primary mr-2 mt-0.5" />
                                      <div className="min-w-0 flex-1">
                                        <h4 className="font-medium text-sm">{ao.titre}</h4>
                                        <p className="text-xs text-muted-foreground">{ao.reference}</p>
                                      </div>
                                    </div>
                                    {getStatusBadge(ao.date_limite)}
                                  </div>
                                  
                                  {ao.interlocuteur_da && <div className="text-xs text-muted-foreground">
                                      <span className="font-medium">Interlocuteur DA:</span>
                                      <span className="ml-1">{ao.interlocuteur_da}</span>
                                    </div>}
                                </div>)}
                            </div>
                          </SheetContent>}
                      </Sheet>;
              })}
                </div>
                
              </CardContent>
            </Card>
          </>}

        {/* Résumé */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle>Résumé de l'année {selectedYear}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">
                  {appelOffres.filter(ao => new Date(ao.date_limite).getFullYear() === selectedYear).length}
                </div>
                <div className="text-sm text-muted-foreground">Total AO</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-warning">
                  {appelOffres.filter(ao => {
                  const dateLimite = new Date(ao.date_limite);
                  const today = new Date();
                  const diffDays = Math.ceil((dateLimite.getTime() - today.getTime()) / (1000 * 3600 * 24));
                  return dateLimite.getFullYear() === selectedYear && diffDays <= 7 && diffDays >= 0;
                }).length}
                </div>
                <div className="text-sm text-muted-foreground">Urgents</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-destructive">
                  {appelOffres.filter(ao => {
                  const dateLimite = new Date(ao.date_limite);
                  const today = new Date();
                  return dateLimite.getFullYear() === selectedYear && dateLimite < today;
                }).length}
                </div>
                <div className="text-sm text-muted-foreground">Expirés</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>;
};
export default PlanningAO;